"use client";
"use no memo";

import { useQuery } from "@apollo/client/react";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { LuBadgeDollarSign } from "react-icons/lu";
import { useDebounce } from "use-debounce";

import TrendingCoinsTable from "@/components/app/trade/trending-coins/trending-coins-table";
import TablePagination from "@/components/shared/table-pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useFiltersAndSort } from "@/contexts/filters-sort-context";
import type { DaoCoinStat, DaoCoinStatFilter } from "@/graphql/codegen/graphql";
import {
  DaoCoinStatsDocument,
  DaoCoinStatsOrderBy,
} from "@/graphql/codegen/graphql";
import {
  DESO_APP_NAME,
  FOCUS_LAUNCH_DATE,
  TRADE_EXCLUDED_PUBLIC_KEYS,
} from "@/utils/constants";
import { getOrderByKey } from "@/utils/trade";

import type { TrendingSortKeys, TrendingTabSelection } from "./constants";
import {
  COLUMNS_WIDTH,
  DaoCoinStatsOrderByPrice,
  DaoCoinStatsOrderByVolume,
  trendingTabs,
  TrendingTimeRange,
} from "./constants";
import { columns as tableColumns } from "./trending-coins-columns";

export const INITIAL_FILTERS_AND_SORTS = {
  initialFilters: [],
  initialSort: null,
};

const TrendingCoinsTableWrapper = () => {
  const [activeTimeRangeTab, setActiveTimeRangeTab] =
    useState<TrendingTimeRange>(TrendingTimeRange["24h"]);
  const [pageOffset, setPageOffset] = useState(0);
  const [itemsPerPage] = useState(100);
  const [columnSorting, setColumnSorting] = useState<SortingState>([
    {
      id: DaoCoinStatsOrderByVolume.Past24H,
      desc: true,
    },
  ]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: itemsPerPage,
  });

  const { currentSort } = useFiltersAndSort<TrendingSortKeys>(
    INITIAL_FILTERS_AND_SORTS,
  );

  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce(searchValue, 300)[0];

  const columns = useMemo(
    () =>
      tableColumns({
        selectedTab:
          trendingTabs.find((tab) => tab.timeDuration === activeTimeRangeTab) ||
          trendingTabs[1],
      }),
    [activeTimeRangeTab],
  );

  const trendingQueryVariables = useMemo(() => {
    const filter: DaoCoinStatFilter = {
      coinUsername: {
        startsWithInsensitive: debouncedSearch,
        notEqualTo: "",
      },
      coinPublicKey: {
        notIn: TRADE_EXCLUDED_PUBLIC_KEYS,
      },
      or: [
        {
          tokenAmmCreatedAt: {
            isNull: true,
          },
        },
        {
          tokenAmmCreatedAt: {
            greaterThan: FOCUS_LAUNCH_DATE,
          },
        },
      ],
    };

    if (currentSort) {
      Object.assign(filter, {
        [currentSort.name]: {
          equalTo: true,
        },
        isBlacklisted: {
          equalTo: false,
        },
      });
    }

    return {
      first: itemsPerPage,
      offset: pageOffset,
      orderBy: getOrderByKey(
        columnSorting,
        DaoCoinStatsOrderBy,
        DaoCoinStatsOrderBy.VolumeUsdPast_24HDesc,
      ),
      filter,
    };
  }, [columnSorting, currentSort, debouncedSearch, itemsPerPage, pageOffset]);

  const {
    data: trendingData,
    loading: loadingTrendingCoins,
    refetch: refetchTrendingData,
  } = useQuery(DaoCoinStatsDocument, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    variables: trendingQueryVariables,
  });

  const trendingCoins =
    (trendingData?.daoCoinStats?.nodes as DaoCoinStat[] | undefined) ?? [];
  const totalCount = trendingData?.daoCoinStats?.totalCount ?? 0;
  const isFirstLoad = loadingTrendingCoins && !trendingData;

  const resetPagination = () => {
    setPagination({
      pageIndex: 0,
      pageSize: itemsPerPage,
    });
    setPageOffset(0);
  };

  const updateColumnSorting = (
    tab: TrendingTimeRange,
    accessorKey: keyof TrendingTabSelection,
    defaultKey: DaoCoinStatsOrderByVolume | DaoCoinStatsOrderByPrice,
  ) => {
    const currentTab = trendingTabs.find(
      (trendingTab) => trendingTab.timeDuration === tab,
    );

    setColumnSorting((oldVal) => [
      {
        id: currentTab?.[accessorKey] || defaultKey,
        desc: oldVal[0].desc,
      },
    ]);
  };

  const handleTimeRangeTab = (tab: TrendingTimeRange) => {
    if (
      Object.values(DaoCoinStatsOrderByVolume).includes(
        columnSorting[0]?.id as DaoCoinStatsOrderByVolume,
      )
    ) {
      updateColumnSorting(
        tab,
        "volumeAccessorKey",
        DaoCoinStatsOrderByVolume.Past24H,
      );
    } else if (
      Object.values(DaoCoinStatsOrderByPrice).includes(
        columnSorting[0]?.id as DaoCoinStatsOrderByPrice,
      )
    ) {
      updateColumnSorting(
        tab,
        "priceAccessorKey",
        DaoCoinStatsOrderByPrice.Past24H,
      );
    }

    setActiveTimeRangeTab(tab);
    resetPagination();
  };

  const handleColumnSortingChange = (sorting: SortingState) => {
    setColumnSorting(sorting);
    resetPagination();
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    resetPagination();
  };

  const onPaginate = (nextPageIndex: number) => {
    const offset = nextPageIndex * itemsPerPage;

    setPagination({
      pageIndex: nextPageIndex,
      pageSize: itemsPerPage,
    });
    setPageOffset(offset);
  };

  const refetchWithUpdatedParams = async () => {
    await refetchTrendingData();
  };

  return isFirstLoad ? (
    <div className="flex flex-col">
      <div
        className="
          flex w-full flex-col items-start justify-normal
          md:flex-row md:items-end md:justify-between
        "
      >
        <Skeleton
          className="
            mb-4 h-[24px] w-full
            md:mb-0 md:w-[150px]
          "
        />
        <Skeleton
          className="
            h-[38px] w-full
            md:w-[430px]
          "
        />
      </div>
      <Skeleton className="mt-4 h-[620px] w-full" />
    </div>
  ) : (
    <>
      <div
        className="
          mb-4 flex flex-col items-start justify-between
          md:flex-row md:items-end
        "
      >
        <div
          className="
            mb-4 font-medium text-muted
            md:mb-0
          "
        >
          <h3 className="flex items-center">
            <LuBadgeDollarSign className="mr-2 text-xl" />
            {DESO_APP_NAME} Tokens
          </h3>
        </div>
        <div
          className="
            flex w-full flex-col justify-between gap-6
            sm:flex-row sm:items-center sm:gap-2
            md:w-auto md:justify-normal
          "
        >
          <div className="flex gap-x-1.5">
            {Object.values(TrendingTimeRange).map((tab) => (
              <Button
                key={tab}
                onClick={() => handleTimeRangeTab(tab)}
                variant={
                  activeTimeRangeTab === tab ? "outlinePrimary" : "outline"
                }
                disabled={loadingTrendingCoins}
                size="lg"
                className="px-4 py-2"
              >
                {TrendingTimeRange[tab]}
              </Button>
            ))}
          </div>

          <div
            className="
              w-full
              sm:w-[200px]
            "
          >
            <Input
              placeholder="Search any token..."
              value={searchValue}
              onInput={(event) => {
                handleSearchChange(event.currentTarget.value);
              }}
            />
          </div>
        </div>
      </div>

      <TrendingCoinsTable
        loading={loadingTrendingCoins}
        columns={columns}
        data={trendingCoins}
        columnSorting={columnSorting}
        setColumnSorting={(sorting) =>
          handleColumnSortingChange(sorting as SortingState)
        }
        pagination={pagination}
        pageOffset={pageOffset}
        columnsWidth={COLUMNS_WIDTH}
        refetchData={refetchWithUpdatedParams}
      />

      <TablePagination
        loading={loadingTrendingCoins}
        pagination={pagination}
        onPaginate={onPaginate}
        itemsPerPage={itemsPerPage}
        totalCount={totalCount}
      />
    </>
  );
};

export default TrendingCoinsTableWrapper;
